import type { JSONContent } from '@tiptap/react';
import { useLayoutEffect, useRef, useState } from 'react';

import { useI18nContext } from '@/i18n/i18n-react';
import type { CouponPosition } from '@/lib/app-editor/coupon-banner-form';
import { cn } from '@/lib/utils';

export interface CouponBannerPreviewProps {
  bannerText: JSONContent;
  backgroundColorHex: string;
  foregroundColorHex: string;
  animated: boolean;
  /** Where the store places the banner; observable as `data-position`. */
  position: CouponPosition;
  /** Tap on the banner (the editor goes to the text tab). */
  onPress?: () => void;
}

// The app (mobile `CouponBanner`) scrolls one copy of the text, 6pt gap
// included, every 30ms per point: ~33pt/s. Its 12pt text is drawn here at
// 10px, so the strip moves one copy at 36ms per pixel and reads as fast.
const APP_MS_PER_POINT = 30;
const POINTS_PER_PIXEL = 12 / 10;
const STRIP_REPEATS = 14;

/** Layout width of the element (0 until laid out, e.g. in jsdom). */
function useWidth<T extends HTMLElement>(enabled: boolean) {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!enabled || !element) return;
    // Layout width: the Studio's zoom transforms scale the strip with it.
    const measure = () => setWidth(element.offsetWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [enabled]);
  return [ref, width] as const;
}

function renderContent(nodes: JSONContent[] | undefined): React.ReactNode[] {
  return (nodes ?? []).map((node, index) => {
    if (node.text === undefined) {
      return <span key={index}>{renderContent(node.content)}</span>;
    }
    return node.marks?.some((mark) => mark.type === 'bold') ? (
      <strong key={index}>{node.text}</strong>
    ) : (
      node.text
    );
  });
}

function BannerText({
  bannerText,
  className,
  ref,
}: {
  bannerText: JSONContent;
  className?: string;
  ref?: React.Ref<HTMLSpanElement>;
}) {
  return (
    <span ref={ref} className={cn('shrink-0 whitespace-pre', className)}>
      {renderContent(bannerText.content)}
    </span>
  );
}

/** The coupon banner as the app draws it: a strip with the text and "COPIAR". */
export function CouponBannerPreview({
  bannerText,
  backgroundColorHex,
  foregroundColorHex,
  animated,
  position,
  onPress,
}: CouponBannerPreviewProps) {
  const { LL } = useI18nContext();
  const [copyRef, copyWidth] = useWidth<HTMLSpanElement>(animated);

  const content = (
    <>
      <span
        data-banner-text
        className="flex min-w-0 flex-1 overflow-hidden whitespace-nowrap text-ellipsis"
      >
        {animated ? (
          <span
            data-banner-strip
            className="flex h-full animate-[coupon-strip_linear_infinite] will-change-transform motion-reduce:animate-none"
            style={
              {
                '--strip-shift': `${-copyWidth}px`,
                animationDuration: `${copyWidth * POINTS_PER_PIXEL * APP_MS_PER_POINT}ms`,
              } as React.CSSProperties
            }
          >
            {Array.from({ length: STRIP_REPEATS }, (_, index) => (
              <BannerText
                key={index}
                ref={index === 0 ? copyRef : undefined}
                bannerText={bannerText}
                // The app's 6pt gap.
                className="pr-[5px]"
              />
            ))}
          </span>
        ) : (
          <BannerText bannerText={bannerText} />
        )}
      </span>
      <span
        style={{
          backgroundColor: foregroundColorHex,
          color: backgroundColorHex,
        }}
        className="shrink-0 rounded-full px-2 py-0.5 text-[8px] font-bold"
      >
        COPIAR
      </span>
    </>
  );
  const shared = {
    'data-coupon-banner': true,
    'data-position': position,
    'data-animated': animated,
    style: { backgroundColor: backgroundColorHex, color: foregroundColorHex },
    className:
      'flex min-h-7 w-full items-center gap-2 px-2 py-1 text-left text-[10px]',
  };

  // Without `onPress` (a thumbnail inside another button) it is not a control.
  if (!onPress) return <div {...shared}>{content}</div>;
  return (
    <button
      type="button"
      {...shared}
      aria-label={LL.couponEditor.canvas.editText()}
      onClick={onPress}
      className={cn(
        shared.className,
        'outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring'
      )}
    >
      {content}
    </button>
  );
}
